import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tmsApi, type WorkflowPolicy, type WorkflowPolicyConfig } from '../client';

export const workflowPolicyKeys = {
  all: ['tms', 'workflowPolicy'] as const,
  byOrganisation: (organisationId: string) =>
    ['tms', 'workflowPolicy', 'org', organisationId] as const,
};

/**
 * Every track's effective policy for an organisation. One query for the whole
 * screen — it switches between tracks locally, so a per-track query would be
 * three round trips for one page.
 */
export function useWorkflowPolicies(organisationId: string | null) {
  return useQuery<WorkflowPolicy[], Error>({
    queryKey: organisationId
      ? workflowPolicyKeys.byOrganisation(organisationId)
      : workflowPolicyKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getWorkflowPolicies(organisationId);
    },
    enabled: !!organisationId,
    // Settings change rarely and the screen is the only thing that writes them,
    // so a short stale time would only add refetches nobody asked for.
    staleTime: 5 * 60 * 1000,
  });
}

/** PUT one track. Invalidates the organisation's set so the others stay honest. */
export function useSaveWorkflowPolicy() {
  const qc = useQueryClient();
  return useMutation<
    WorkflowPolicy,
    Error,
    { organisationId: string; trackKey: string; config: WorkflowPolicyConfig }
  >({
    mutationKey: [...workflowPolicyKeys.all, 'save'],
    mutationFn: ({ organisationId, trackKey, config }) =>
      tmsApi.saveWorkflowPolicy(organisationId, trackKey, config),
    onSuccess: (_policy, { organisationId }) => {
      qc.invalidateQueries({ queryKey: workflowPolicyKeys.byOrganisation(organisationId) });
    },
  });
}
